import React, { useRef, forwardRef, useMemo, useCallback, useLayoutEffect } from 'react'
import { scrollToSelectedItem, getFlattenedSuggestions } from './utils'
import { Icon, iconProps, useUpdateRef } from '@zambezi/sdk/common'

/**
 * Create a React Select Input component
 * @template T
 * @template Item
 * @param {import('./interfaces').SelectInputProps<T, Item>} props
 * @param {React.Ref} ref
 * @returns {JSX.Element}
 */
function selectInput (
  {
    renderInput,
    elementRef,
    disableHighlightByKey,
    suggestions,
    disabledItemsIndices,
    withGrouping,
    getGroupChildren,
    getSuggestionValue,
    onFocus,
    onBlur,
    onClick,
    onKeyDown,
    onMouseDown,
    highlightBy,
    iconProps,
    ...props
  },
  ref
) {
  const { isOpen, isDisabled, isFocused } = props
  const wasOpenRef = useRef(false)
  const didOpen = !!isOpen && !wasOpenRef.current
  /** @type {React.RefObject<HTMLElement>} */
  const inputRef = useRef()
  const disabledHandler = isDisabled ? null : true
  const flattenedSuggestions = useMemo(
    () =>
      disableHighlightByKey
        ? []
        : getFlattenedSuggestions(
          suggestions,
          withGrouping,
          highlightBy || getGroupChildren,
          getSuggestionValue
        ),
    [
      disableHighlightByKey,
      suggestions,
      withGrouping,
      getGroupChildren,
      getSuggestionValue,
      highlightBy
    ]
  )

  /** @type {React.KeyboardEventHandler} */
  const handleKeyDown = useCallback(
    (event) => {
      if (!disableHighlightByKey) {
        const hasDisabledItems = !!disabledItemsIndices.length
        const { key } = event
        const matchKey = key.length === 1 ? key.toLowerCase() : null
        const suggestions = flattenedSuggestions
        const suggestionsCount = suggestions.length

        if (matchKey && suggestionsCount) {
          const lookup = elementRef.current
          const focusIndex = lookup.getFocusIndex()
          const statingIndex = focusIndex === null ? -1 : focusIndex

          for (let i = 0; i < suggestionsCount; i++) {
            const itemIndex = (i + statingIndex + 1) % suggestionsCount
            if (
              (!hasDisabledItems ||
                disabledItemsIndices.indexOf(itemIndex) === -1) &&
              suggestions[itemIndex].slice(0, 1).toLowerCase() === matchKey
            ) {
              lookup.setFocusIndex(itemIndex)
              break
            }
          }
        }
      }
      onKeyDown(event)
    },
    [
      onKeyDown,
      disableHighlightByKey,
      flattenedSuggestions,
      disabledItemsIndices
    ]
  )
  /** @type {React.MouseEventHandler} */
  const handleIconMouseDown = useCallback(
    (event) => {
      const element = elementRef.current
      const input = inputRef.current
      event.preventDefault()
      input && (input.element || input).focus()
      isFocused && isOpen ? element.close() : element.open()
    },
    [isOpen, isFocused]
  )
  /** @type {React.MouseEventHandler} */
  const handleMouseDown = useCallback(
    (event) => {
      const element = elementRef.current
      if (isFocused) {
        isOpen ? element.close() : element.open()
      } else {
        element.open()
      }
      onMouseDown && onMouseDown(event)
    },
    [isFocused, isOpen, onMouseDown]
  )

  wasOpenRef.current = !!isOpen
  useUpdateRef(ref, inputRef)
  useLayoutEffect(() => {
    if (didOpen) {
      elementRef.current && scrollToSelectedItem(elementRef.current.element || elementRef.current)
    }
  }, [didOpen])

  return (
    <div
      className='zb-select-input-wrapper'
      onKeyDown={disabledHandler && handleKeyDown}
    >
      {renderInput(
        Object.assign({}, props, {
          ref: inputRef,
          'aria-autocomplete': null,
          onMouseDown: disabledHandler && handleMouseDown
        })
      )}
      <Icon
        name={isOpen ? 'core-select-expanded' : 'core-select-collapsed'}
        aria-hidden='true'
        title=''
        iconProps={iconProps}
        onMouseDown={disabledHandler && handleIconMouseDown}
      />
    </div>
  )
}

/**
 * @template T
 * @template Item
 * @type {React.ForwardRefExoticComponent<import('./interfaces').SelectInputProps<T, Item> & React.RefAttributes<HTMLElement>>}
 */
const SelectInput = forwardRef(selectInput)
SelectInput.displayName = 'SelectInput'
SelectInput.propTypes = {
  iconProps
}

export default SelectInput
